import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, AlertTriangle, FileText, Play, Upload, Loader2, Link as LinkIcon,
  CheckCircle2, XCircle,
} from 'lucide-react';
import { API } from '@/App';

/**
 * Shared Benchmark Validation panel (Phase I). Used inside every PhenoScan sub-tab.
 *
 * Props:
 *   subTab — one of 'leaf','seed','disease','panicle','root','nir'
 *   subTabLabel — e.g. 'Leaf & Canopy'
 */
export default function ValidationPanel({ subTab, subTabLabel }) {
  const [meta, setMeta] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState({});
  const [customMode, setCustomMode] = useState(false);
  const [userCitation, setUserCitation] = useState('');
  const gtRef = useRef(null);
  const filesRef = useRef(null);
  const [gtFile, setGtFile] = useState(null);
  const [imgFiles, setImgFiles] = useState([]);
  const cfg = { withCredentials: true };

  const setB = (k, v) => setBusy(p => ({ ...p, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/phenoscan/validation/registry`, cfg)
      .then(r => setMeta(r.data[subTab] || null))
      .catch(() => {});
  }, [subTab]);

  const runBundled = useCallback(async () => {
    setB('run', true); setResult(null);
    try {
      const r = await axios.post(`${API}/phenoscan/validation/${subTab}/run`, {}, cfg);
      setResult(r.data);
    } catch (e) {
      toast.error(`Validation failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('run', false); }
  }, [subTab]);

  const runCustom = useCallback(async () => {
    if (!gtFile) { toast.error('Select a ground-truth CSV'); return; }
    if (!imgFiles.length && subTab !== 'nir') { toast.error('Select at least one image'); return; }
    setB('run', true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('ground_truth_csv', gtFile);
      fd.append('user_citation', userCitation);
      imgFiles.forEach(f => fd.append('files', f));
      const r = await axios.post(`${API}/phenoscan/validation/${subTab}/custom`, fd,
        { ...cfg, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
    } catch (e) {
      toast.error(`Custom validation failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('run', false); }
  }, [subTab, gtFile, imgFiles, userCitation]);

  const downloadReport = useCallback(async () => {
    setB('pdf', true);
    try {
      const r = await axios.post(`${API}/phenoscan/validation/${subTab}/report`, {},
        { ...cfg, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `BKAgriD_Validation_${subTabLabel.replace(/\s+/g, '_').replace(/\//g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Report failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setB('pdf', false); }
  }, [subTab, subTabLabel]);

  const tierLabel = useMemo(() => {
    if (!meta) return '';
    if (customMode && result?.tier === 'custom') return 'User-supplied reference set';
    return meta.tier === 'external'
      ? 'External peer-reviewed reference'
      : 'Algorithmic precision benchmark (synthetic)';
  }, [meta, customMode, result]);

  if (!meta) return null;

  return (
    <div className="rounded-xl p-4 mt-4" style={{ background: '#F9FAF7', border: '1px solid #D6CFC0' }}
      data-testid={`validation-panel-${subTab}`}>
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck size={16} style={{ color: '#2D6A4F' }} />
        <div className="font-display text-base" style={{ color: '#1B4332' }}>
          Benchmark Validation
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded"
          style={{
            background: meta.tier === 'external' ? '#D1FAE5' : '#FEF3C7',
            color: meta.tier === 'external' ? '#065F46' : '#78350F',
          }}>
          {tierLabel}
        </span>
      </div>

      <div className="text-xs mb-3" style={{ color: '#4F4B44' }}>
        <div><b>Bundled benchmark:</b> {meta.name}</div>
        <div><b>Reference method:</b> {meta.reference_method}</div>
        <div><b>Target:</b> {meta.target_description}</div>
        <div className="text-[11px] mt-1" style={{ color: '#6F695F' }}>
          <LinkIcon size={10} className="inline mr-1" />
          <span style={{ whiteSpace: 'pre-wrap' }}>{meta.citation}</span>
        </div>
      </div>

      {/* Mode switch */}
      <div className="flex gap-3 text-xs mb-3" data-testid={`validation-mode-${subTab}`}>
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={!customMode} onChange={() => setCustomMode(false)} />
          <span>Bundled reference</span>
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={customMode} onChange={() => setCustomMode(true)} />
          <span>Upload your own validation set</span>
        </label>
      </div>

      {customMode ? (
        <CustomValidationUpload
          subTab={subTab}
          gtFile={gtFile} setGtFile={setGtFile}
          gtRef={gtRef} filesRef={filesRef}
          imgFiles={imgFiles} setImgFiles={setImgFiles}
          userCitation={userCitation} setUserCitation={setUserCitation}
          onRun={runCustom} busy={busy.run}
        />
      ) : (
        <div className="flex gap-2">
          <button onClick={runBundled} disabled={busy.run}
            data-testid={`validation-run-${subTab}`}
            className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
            style={{ background: '#2D6A4F', color: 'white' }}>
            {busy.run ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            Run bundled benchmark
          </button>
          <button onClick={downloadReport} disabled={busy.pdf}
            data-testid={`validation-report-${subTab}`}
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
            style={{ background: 'white', border: '1px solid #2D6A4F', color: '#2D6A4F' }}>
            {busy.pdf ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
            Download validation report PDF
          </button>
        </div>
      )}

      {result && <ResultBlock result={result} onFailExplanation={meta.on_fail_explanation} />}
    </div>
  );
}


function CustomValidationUpload({
  subTab, gtFile, setGtFile, gtRef, filesRef, imgFiles, setImgFiles,
  userCitation, setUserCitation, onRun, busy,
}) {
  const gtSchema = {
    leaf: 'filename, truth_area_cm2',
    seed: 'filename, truth_count',
    disease: 'filename, truth_severity_pct',
    panicle: 'filename, truth_length_cm',
    root: 'filename, truth_diameter_mm, truth_length_cm, (optional: manual_px_per_cm)',
    nir: '(Custom NIR not supported — bundled benchmark only)',
  }[subTab];

  if (subTab === 'nir') {
    return (
      <div className="text-xs p-3 rounded" style={{ background: '#FEF3C7', color: '#78350F' }}>
        Custom NIR validation is not supported. Use the bundled Eigenvector held-out
        benchmark or re-train a model and re-run the bundled benchmark.
      </div>
    );
  }

  return (
    <div className="text-xs space-y-2 rounded p-3" style={{ background: '#FFFFFF', border: '1px solid #E5E5E0' }}>
      <div style={{ color: '#4F4B44' }}>
        <b>Expected ground-truth CSV columns:</b> <code>{gtSchema}</code>
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="px-2 py-1 rounded cursor-pointer flex items-center gap-1"
          style={{ border: '1px solid #E5E5E0' }}
          data-testid={`validation-custom-gt-${subTab}`}>
          <Upload size={11} />
          <span>{gtFile ? gtFile.name : 'Ground-truth CSV'}</span>
          <input ref={gtRef} type="file" accept=".csv" className="hidden"
            onChange={e => setGtFile(e.target.files?.[0] || null)} />
        </label>
        <label className="px-2 py-1 rounded cursor-pointer flex items-center gap-1"
          style={{ border: '1px solid #E5E5E0' }}
          data-testid={`validation-custom-files-${subTab}`}>
          <Upload size={11} />
          <span>{imgFiles.length ? `${imgFiles.length} image(s)` : 'Reference images'}</span>
          <input ref={filesRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => setImgFiles(Array.from(e.target.files || []))} />
        </label>
      </div>
      <div>
        <label className="block text-[11px] mb-1" style={{ color: '#4F4B44' }}>
          Your citation (appears in the PDF report)
        </label>
        <input type="text" value={userCitation} onChange={e => setUserCitation(e.target.value)}
          data-testid={`validation-custom-citation-${subTab}`}
          placeholder="e.g., Lab X, gravimetric method, 2026-02-15"
          className="px-2 py-1 rounded text-xs w-full"
          style={{ border: '1px solid #E5E5E0' }} />
      </div>
      <button onClick={onRun} disabled={busy}
        data-testid={`validation-custom-run-${subTab}`}
        className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5"
        style={{ background: '#2D6A4F', color: 'white' }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
        Run custom validation
      </button>
    </div>
  );
}


function ResultBlock({ result, onFailExplanation }) {
  const rows = result.rows || [];
  const summary = result.summary || {};
  const passed = result.passed;
  const cols = rows.length ? Object.keys(rows[0]) : [];
  return (
    <div className="mt-3" data-testid={`validation-result-${result.sub_tab}`}>
      <div className="flex items-center gap-2 mb-2">
        {passed ? (
          <span className="px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1"
            style={{ background: '#D1FAE5', color: '#065F46' }}
            data-testid={`validation-status-${result.sub_tab}`}>
            <CheckCircle2 size={12} /> PASS
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1"
            style={{ background: '#FEE2E2', color: '#991B1B' }}
            data-testid={`validation-status-${result.sub_tab}`}>
            <XCircle size={12} /> FAIL — target not met
          </span>
        )}
        <span className="text-[11px]" style={{ color: '#6F695F' }}>
          n = {rows.length} · digest <code>{(result.digest || '').slice(0, 12)}</code>
        </span>
      </div>
      {!passed && onFailExplanation && (
        <div className="rounded-lg p-3 mb-3 text-xs whitespace-pre-line"
          style={{ background: '#FFFBEB', border: '1px solid #FDE68A', color: '#78350F' }}
          data-testid={`validation-fail-explanation-${result.sub_tab}`}>
          <div className="font-semibold mb-1" style={{ color: '#92400E' }}>
            What this result means
          </div>
          {onFailExplanation}
        </div>
      )}
      <div className="overflow-x-auto rounded border" style={{ borderColor: '#E5E5E0', maxHeight: 280 }}>
        <table className="text-[11px]">
          <thead><tr style={{ background: '#F3F6F3', position: 'sticky', top: 0 }}>
            {cols.map(c => <th key={c} className="px-2 py-1 text-left">{c}</th>)}
          </tr></thead>
          <tbody>
            {rows.slice(0, 30).map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid #F3F6F3' }}>
                {cols.map(c => (
                  <td key={c} className="px-2 py-0.5">
                    {typeof r[c] === 'number' ? r[c].toFixed(3) :
                      r[c] === null || r[c] === undefined ? '—' : String(r[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
        {Object.entries(summary)
          .filter(([k, v]) => !Array.isArray(v) && typeof v !== 'object')
          .slice(0, 8)
          .map(([k, v]) => (
            <div key={k} className="rounded p-2" style={{ background: '#F3F6F3' }}>
              <div className="text-[10px] uppercase" style={{ color: '#6F695F', letterSpacing: 0.5 }}>
                {k}
              </div>
              <div className="font-semibold text-sm" style={{ color: '#1B4332' }}>
                {typeof v === 'number' ? v.toFixed(4) : String(v)}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
